import { hasValue } from '@repo/utils';

import type { FactTrajectoryEntity } from '../../service/entities';
import type { TrajectoryPlanOrFact } from '../../service/model';
import type { TGraphTrajectoryView, TTrajectoryExcludeReason } from '../../types/graph-trajectory';
import { getTrajectoryLineColor } from '../../utils/trajectory-line-colors';

import { computeOffsetDeltas } from './utils/trajectory-offset-deltas';
import {
  areProjectedCrsCompatible,
  getTrajectoryOrigin,
  hasSpatialOrigin,
  hasVerticalOrigin,
} from './utils/trajectory-origin';

export type TReferenceTrajectoryRequest = {
  wellboreId: number;
  trajectoryIds: ReadonlySet<number>;
};

function readAziVertSectDeg(entity: FactTrajectoryEntity): number | null {
  const value = entity.data.aziVertSect?.value;

  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }

  return value;
}

function toGraphView(params: {
  entity: FactTrajectoryEntity;
  role: TGraphTrajectoryView['role'];
  wellName: string | null;
  planOrFact: TrajectoryPlanOrFact;
  deltas: TGraphTrajectoryView['deltas'];
  canPlotSpatial: boolean;
  canPlotVertical: boolean;
  spatialExcludeReason: TTrajectoryExcludeReason | null;
  verticalExcludeReason: TTrajectoryExcludeReason | null;
}): TGraphTrajectoryView {
  const {
    entity,
    role,
    wellName,
    planOrFact,
    deltas,
    canPlotSpatial,
    canPlotVertical,
    spatialExcludeReason,
    verticalExcludeReason,
  } = params;

  return {
    id: entity.id,
    title: entity.title,
    wellboreId: entity.wellboreId,
    role,
    color: getTrajectoryLineColor(entity.id),
    entity,
    wellName,
    planOrFact,
    objectVersion: entity.data.objectVersion ?? null,
    deltas,
    canPlotSpatial,
    canPlotVertical,
    spatialExcludeReason,
    verticalExcludeReason,
  };
}

function resolveWellName(
  wellboreId: number,
  wellNamesByWellboreId: ReadonlyMap<number, string> | undefined,
): string | null {
  const name = wellNamesByWellboreId?.get(wellboreId);

  return typeof name === 'string' && name.trim() ? name : null;
}

function resolveSpatialExcludeReason(
  activeOrigin: ReturnType<typeof getTrajectoryOrigin>,
  offsetOrigin: ReturnType<typeof getTrajectoryOrigin>,
): TTrajectoryExcludeReason | null {
  if (!activeOrigin || !offsetOrigin) {
    return 'missingOrigin';
  }

  if (!hasSpatialOrigin(activeOrigin) || !hasSpatialOrigin(offsetOrigin)) {
    return 'missingOrigin';
  }

  if (!areProjectedCrsCompatible(activeOrigin.projectedCrsKey, offsetOrigin.projectedCrsKey)) {
    return 'crsMismatch';
  }

  return null;
}

function resolveVerticalExcludeReason(
  activeOrigin: ReturnType<typeof getTrajectoryOrigin>,
  offsetOrigin: ReturnType<typeof getTrajectoryOrigin>,
): TTrajectoryExcludeReason | null {
  if (!activeOrigin || !offsetOrigin) {
    return 'missingOrigin';
  }

  if (!hasVerticalOrigin(activeOrigin) || !hasVerticalOrigin(offsetOrigin)) {
    return 'missingTvdss';
  }

  return null;
}

export function buildGraphTrajectoryViews(params: {
  planOrFact: TrajectoryPlanOrFact | null;
  selectedTrajectoryId: number | null;
  selectedWellboreId: number | null;
  wellboreTrajectories: FactTrajectoryEntity[];
  referenceEntitiesByWellbore: ReadonlyMap<number, FactTrajectoryEntity[]>;
  referenceTrajectoryRequests: readonly TReferenceTrajectoryRequest[];
  /** Optional well names keyed by wellbore id (active + references). */
  wellNamesByWellboreId?: ReadonlyMap<number, string>;
}): { main: TGraphTrajectoryView | null; references: TGraphTrajectoryView[]; activeAziVertSectDeg: number | null } {
  if (params.planOrFact === 'plan') {
    return { main: null, references: [], activeAziVertSectDeg: null };
  }

  const planOrFact: TrajectoryPlanOrFact = 'fact';
  const mainId = params.selectedTrajectoryId;
  const mainWellboreId = params.selectedWellboreId;
  let main: TGraphTrajectoryView | null = null;
  const references: TGraphTrajectoryView[] = [];
  const seenIds = new Set<number>();

  if (hasValue(mainId) && hasValue(mainWellboreId)) {
    const mainEntity = params.wellboreTrajectories.find((item) => item.id === mainId) ?? null;

    if (mainEntity) {
      const origin = getTrajectoryOrigin(mainEntity);
      const canPlotSpatial = origin !== null && hasSpatialOrigin(origin);
      const canPlotVertical = origin !== null && hasVerticalOrigin(origin);

      main = toGraphView({
        entity: mainEntity,
        role: 'main',
        wellName: resolveWellName(mainEntity.wellboreId, params.wellNamesByWellboreId),
        planOrFact,
        deltas: null,
        canPlotSpatial,
        canPlotVertical,
        spatialExcludeReason: canPlotSpatial ? null : 'missingOrigin',
        verticalExcludeReason: canPlotVertical ? null : 'missingTvdss',
      });
      seenIds.add(mainEntity.id);
    }
  }

  const activeOrigin = main ? getTrajectoryOrigin(main.entity) : null;
  const activeAziVertSectDeg = main ? readAziVertSectDeg(main.entity) : null;

  for (const request of params.referenceTrajectoryRequests) {
    if (request.wellboreId === mainWellboreId) {
      continue;
    }

    const entities = params.referenceEntitiesByWellbore.get(request.wellboreId) ?? [];

    for (const entity of entities) {
      if (seenIds.has(entity.id)) {
        continue;
      }

      const includeAll = request.trajectoryIds.size === 0;

      if (!includeAll && !request.trajectoryIds.has(entity.id)) {
        continue;
      }

      seenIds.add(entity.id);

      const offsetOrigin = getTrajectoryOrigin(entity);
      const spatialExcludeReason = resolveSpatialExcludeReason(activeOrigin, offsetOrigin);
      const verticalExcludeReason = resolveVerticalExcludeReason(activeOrigin, offsetOrigin);
      const canPlotSpatial = spatialExcludeReason === null;
      const canPlotVertical = verticalExcludeReason === null;

      const deltas = activeOrigin && offsetOrigin ? computeOffsetDeltas(activeOrigin, offsetOrigin) : null;

      references.push(
        toGraphView({
          entity,
          role: 'reference',
          wellName: resolveWellName(entity.wellboreId, params.wellNamesByWellboreId),
          planOrFact,
          deltas,
          canPlotSpatial,
          canPlotVertical,
          spatialExcludeReason,
          verticalExcludeReason,
        }),
      );
    }
  }

  return { main, references, activeAziVertSectDeg };
}

export function formatGraphTrajectoryLegendLabel(view: TGraphTrajectoryView, activeLabel: string): string {
  const parts: string[] = [];

  if (view.wellName) {
    parts.push(view.wellName);
  }

  parts.push(view.title);

  if (view.objectVersion) {
    parts.push(`v${view.objectVersion}`);
  }

  parts.push(view.planOrFact);

  if (view.role === 'main' && activeLabel) {
    parts.push(activeLabel);
  }

  return parts.join(' · ');
}
